import { AgFns, GridOptionsExt, GridState } from "@/shared/ag-fns";
import { Injectable } from "@angular/core";
import { Location } from "@angular/common";
import { ActivatedRoute, Route, Router } from "@angular/router";
import _ from "lodash";
import { DomainBaseComponent } from "@/shared/domain-base.component";
import { UtilFns } from "@/shared/util-fns";

// Place to store state about a page

export interface PageState {
  gridState: GridState;
  otherQueryParams: { [key: string]: any };
}

@Injectable({
  providedIn: "root",
})
export class NavigationStateService {
  pageState = new Map<string, PageState>();

  constructor() {}

  setState(key: string, state: PageState): void {
    this.pageState.set(key, state);
  }

  getState(key: string): PageState | undefined {
    return this.pageState.get(key);
  }

  // use any optional params on the route if they exist
  // if none found just use the saved state.
  captureLocation(
    parent: DomainBaseComponent,
    gridOptionsExt: GridOptionsExt,
    keyName: string = null
  ) {
    const route = parent["route"] as ActivatedRoute;
    UtilFns.assertNonNull(route, "parent.route must be set");
    let gridState: GridState;
    let queryParams = route.snapshot.queryParams;

    if (!_.isEmpty(queryParams)) {
      gridState = AgFns.createGridState(queryParams, keyName);
    } else {
      const location = this.getLocation(parent);
      const pathName = this.getPathName(location);
      let otherQueryParams: { [key: string]: any } = {};
      const pageState = this.getState(pathName);
      if (pageState != null) {
        ({ gridState, otherQueryParams } = pageState);
        if (gridState) {
          gridState.keyField = keyName;
        }
        queryParams = { ...gridState, ...otherQueryParams };
      } else {
        queryParams = {};
      }
    }
    AgFns.attachGridState(gridOptionsExt, gridState);
    return queryParams;
  }

  replaceLocation(
    parent: any,
    gridOptionsExt: GridOptionsExt,
    key: any = null,
    otherQueryParams: { [key: string]: any } = {}
  ) {
    const location = this.getLocation(parent);
    const pathName = this.getPathName(location);
    const router = parent.router as Router;

    // update  state cache
    const oldGridState = <GridState>gridOptionsExt?.context?.gridState || {};
    const keyField = oldGridState.keyField;
    const gridState = AgFns.getAndAttachGridState(
      gridOptionsExt,
      key,
      keyField
    );
    this.setState(pathName, {
      gridState: gridState,
      otherQueryParams: otherQueryParams,
    });

    // update the URL
    const urlTree = router.createUrlTree([pathName], {
      queryParams: otherQueryParams,
    });
    // if key is null use the last saved key
    key = key || gridState?.key;
    const url = AgFns.buildGridRouteParamsUrl(
      urlTree,
      gridOptionsExt,
      key && key.toString()
    );
    location.replaceState(url);
  }

  getLocation(parent: any): Location {
    return parent.domainService.location as Location;
  }

  getPathName(location: Location): string {
    return location.path().split("?")[0];
  }
}
